import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CalendarClock, PiggyBank } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

const when = new Intl.DateTimeFormat("en-US", { timeZone: "America/Nassau", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function NotificationBell({ testId = "notification-bell" }) {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const click = async (n) => {
    setOpen(false);
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    load();
    navigate(n.link);
  };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative h-10 w-10 grid place-items-center rounded-xl hover:bg-secondary" aria-label="Notifications" data-testid={testId}>
          <Bell className="h-5 w-5" />
          {data.unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center" data-testid={`${testId}-badge`}>{data.unread > 99 ? "99+" : data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(92vw,360px)] p-0 bg-card rounded-2xl overflow-hidden" data-testid="notification-list">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-heading font-semibold">Notifications</span>
          <button onClick={readAll} disabled={!data.unread} className="text-xs font-semibold text-sky-700 disabled:text-muted-foreground" data-testid="notification-mark-all-read">Mark all as read</button>
        </div>
        <ul className="max-h-[60vh] overflow-y-auto divide-y divide-border">
          {data.items.length === 0 && <li className="px-4 py-6 text-sm text-center text-muted-foreground">No notifications yet</li>}
          {data.items.map((n) => {
            const Icon = n.kind === "bill_reminder" ? CalendarClock : PiggyBank;
            return (
              <li key={n.id}>
                <button onClick={() => click(n)} className={`w-full text-left flex gap-3 px-4 py-3 hover:bg-secondary/50 ${n.read ? "" : "bg-sky-50/60"}`} data-testid={`notification-item-${n.id}`}>
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${n.kind === "bill_reminder" ? "text-sky-600" : "text-amber-600"}`} />
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm ${n.read ? "text-foreground/70" : "font-semibold"}`}>{n.text}</span>
                    <span className="block text-[11px] text-muted-foreground mt-0.5">{when.format(new Date(n.created_at))}{!n.read && " · unread"}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
